import { useEffect, useState } from "react";
import Map, { Marker } from "react-map-gl";
import { MapPin, Loader2 } from "lucide-react";
import "mapbox-gl/dist/mapbox-gl.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useDeviceChangeLog } from "@/hooks/use-device-change-log";

interface ManualLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  device: any;
  initialLat?: number | null;
  initialLng?: number | null;
}

export const ManualLocationDialog = ({
  open,
  onOpenChange,
  device,
  initialLat,
  initialLng,
}: ManualLocationDialogProps) => {
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const isValidCoord = (lat?: number | null, lng?: number | null) =>
    lat != null && lng != null && !isNaN(lat) && !isNaN(lng);
  const [selected, setSelected] = useState<{ lat: number; lng: number } | null>(
    isValidCoord(initialLat, initialLng) ? { lat: initialLat as number, lng: initialLng as number } : null
  );
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();
  const { logChange } = useDeviceChangeLog();

  const center = selected ?? { lat: 54.5, lng: -2.5 };

  useEffect(() => {
    if (!open) return;
    setSelected(
      isValidCoord(initialLat, initialLng) ? { lat: initialLat as number, lng: initialLng as number } : null
    );
  }, [open, initialLat, initialLng]);

  useEffect(() => {
    if (!open || mapboxToken) return;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("getMapboxToken");
        if (error) throw error;
        if (data?.token) setMapboxToken(data.token);
        else throw new Error("No Mapbox token received");
      } catch (e) {
        console.error("Error fetching Mapbox token:", e);
        toast.error("Failed to load map");
      }
    })();
  }, [open, mapboxToken]);

  const handleMapClick = (event: any) => {
    const { lng, lat } = event.lngLat;
    setSelected({ lat, lng });
  };

  const updateLatInput = (value: string) => {
    const lat = parseFloat(value);
    if (isNaN(lat)) return;
    setSelected((prev) => ({ lat, lng: prev?.lng ?? 0 }));
  };

  const updateLngInput = (value: string) => {
    const lng = parseFloat(value);
    if (isNaN(lng)) return;
    setSelected((prev) => ({ lat: prev?.lat ?? 0, lng }));
  };

  const callUpdateDevice = async (
    session: any,
    field: string,
    value: string
  ) => {
    const params = new URLSearchParams();
    params.append("deviceID", device.deviceID);
    params.append("field", field);
    params.append("value", value);
    logChange(device.deviceID, field, value);
    const res = await fetch("https://i56o7w.buildship.run/updateDevice", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: params,
    });
    if (!res.ok) {
      let msg = `Failed to update ${field}`;
      try {
        const err = await res.json();
        msg = err.message || msg;
      } catch {}
      throw new Error(msg);
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    setIsSaving(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("No session found");

      const latStr = selected.lat.toString();
      const lngStr = selected.lng.toString();

      await callUpdateDevice(session, "location.latWGS84", latStr);
      await callUpdateDevice(session, "location.longWGS84", lngStr);
      await callUpdateDevice(session, "location.currentHdop", "1.0");
      await callUpdateDevice(session, "cameraConfig.enableGPS", "0");

      // Mirror to device_details.location JSONB, preserving other keys
      try {
        const { data: current } = await supabase
          .from("device_details")
          .select("location")
          .eq("device_id", device.deviceID)
          .maybeSingle();
        const existing = (current?.location as Record<string, any>) || {};
        const merged = {
          ...existing,
          latWGS84: selected.lat,
          longWGS84: selected.lng,
          currentHdop: 1.0,
        };
        const { error: updateError } = await supabase
          .from("device_details")
          .update({
            location: merged,
            lat_wgs84: selected.lat,
            long_wgs84: selected.lng,
            current_hdop: "1.0",
          })
          .eq("device_id", device.deviceID);
        if (updateError) throw updateError;
      } catch (mirrorErr) {
        console.warn("Failed to mirror manual location to device_details:", mirrorErr);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["device-details", device.deviceID] }),
        queryClient.invalidateQueries({ queryKey: ["device", device.deviceID] }),
        queryClient.invalidateQueries({ queryKey: ["device-assignments"] }),
        queryClient.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "device-assignments-with-names" }),
        queryClient.invalidateQueries({ queryKey: ["devices"] }),
        queryClient.invalidateQueries({ queryKey: ["device-locations"] }),
      ]);
      await queryClient.refetchQueries({ predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "device-assignments-with-names" });

      toast.success("Manual location saved");
      onOpenChange(false);
    } catch (e: any) {
      console.error("Error saving manual location:", e);
      toast.error(e?.message || "Failed to save manual location");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Set Manual Location</DialogTitle>
          <DialogDescription>
            Click on the map to set the device's location. This overrides the device's
            GPS reading and disables on-device GPS. The device will report this position
            until you change it again or re-enable GPS.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="w-full h-[360px] rounded-lg overflow-hidden border">
            {mapboxToken ? (
              <Map
                mapboxAccessToken={mapboxToken}
                initialViewState={{
                  latitude: center.lat,
                  longitude: center.lng,
                  zoom: selected ? 14 : 5,
                }}
                style={{ width: "100%", height: "100%" }}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                onClick={handleMapClick}
                cursor="crosshair"
              >
                {selected && !isNaN(selected.lat) && !isNaN(selected.lng) && (
                  <Marker latitude={selected.lat} longitude={selected.lng}>
                    <div className="w-6 h-6 rounded-full bg-brand-500 border-2 border-white shadow-lg flex items-center justify-center">
                      <MapPin className="h-4 w-4 text-white" />
                    </div>
                  </Marker>
                )}
              </Map>
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <p className="text-sm text-muted-foreground">Loading map...</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Latitude</Label>
              <Input
                type="number"
                step="any"
                value={selected?.lat ?? ""}
                onChange={(e) => updateLatInput(e.target.value)}
                placeholder="e.g. 51.5074"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Longitude</Label>
              <Input
                type="number"
                step="any"
                value={selected?.lng ?? ""}
                onChange={(e) => updateLngInput(e.target.value)}
                placeholder="e.g. -0.1278"
              />
            </div>
          </div>

          {selected && (
            <p className="text-xs text-muted-foreground">
              Selected: {selected.lat.toFixed(6)}, {selected.lng.toFixed(6)}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selected || isSaving}
            className="text-black gap-2"
            style={{ backgroundColor: "#DFEF97" }}
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save Manual Location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
